import React, { useMemo } from 'react';
import { get } from 'lodash';
import { BeneficialOwnerType, TrustApplicantRelatedPartyType } from '@lendi/core-constants';
import { Accordion, AccordionGroup } from '@lendi-ui/accordion';
import Grid from '@lendi-ui/grid';
import { Heading } from '@lendi-ui/typography';

import { FIELD_PATHS, PII_FIELD_MASKING_CLASS } from '@funnels/constants';
import { useAnalytics } from '@funnels/providers';
import { ApplicantEntityIndex, FormValuesApplicants, RelatedParty, StoreApplicant } from '@funnels/types';
import { capitalizeForEnumValue, getApplicantName, getDisplayDateString, getIndexFromId } from '@funnels/utils';

import { StringDisplay } from '../../../Fields';
import { GreyedPanelText } from '../../../Fields/PlainDisplay/style';
import { AccordionContent, AccordionHeaderCircleIcon, AccordionWrapper, PaddedGridUnit } from '../_common/style';
import { Link, LinkContainer } from './layouts.style';

interface TrustApplicantSummaryLayoutProps extends ApplicantEntityIndex {
  goToSection: (clientId: string, section: string) => void;
  applicantIndex: number;
  formValues: FormValuesApplicants;
}

const TrustTrustees = ({
  trustee,
  relatedParties,
  currentApplicantClientId,
  applicants,
  index,
}: {
  trustee: RelatedParty;
  relatedParties: RelatedParty[];
  currentApplicantClientId: string;
  applicants: StoreApplicant[];
  index: number;
}) => {
  const trusteeApplicantId = get({ relatedParties: [trustee] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0));
  const trusteeApplicantIndex = getIndexFromId(applicants, trusteeApplicantId!);
  const trustBeneficiary = relatedParties.find(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)) === trusteeApplicantId &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TRUST_APPLICANT_ID(0)) ===
        currentApplicantClientId &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
        TrustApplicantRelatedPartyType.TrustBeneficiary
  );
  const trustBeneficialOwner = relatedParties.find(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)) === trusteeApplicantId &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TRUST_APPLICANT_ID(0)) ===
        currentApplicantClientId &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
        TrustApplicantRelatedPartyType.TrustBeneficialOwner
  );

  const beneficialOwnerRole = get({ relatedParties: [trustBeneficialOwner] }, FIELD_PATHS.RELATED_PARTY_OWNER_ROLE(0));
  const beneficialOwnerType = get({ relatedParties: [trustBeneficialOwner] }, FIELD_PATHS.RELATED_PARTY_OWNER_TYPE(0));

  const heading =
    get({ applicants }, FIELD_PATHS.APPLICANT_COMPANY_NAME(trusteeApplicantIndex)) ||
    getApplicantName(applicants, trusteeApplicantId) ||
    `Trustee ${index + 1}`;

  return (
    <Grid gutter="nil">
      <PaddedGridUnit size={1}>
        <Heading size="xxs">{heading}</Heading>
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <StringDisplay title="Beneficiary" value={trustBeneficiary ? 'Yes' : 'No'} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <StringDisplay title="Beneficial owner" value={trustBeneficialOwner ? 'Yes' : 'No'} />
      </PaddedGridUnit>
      {!!trustBeneficialOwner && (
        <>
          <PaddedGridUnit size={1 / 2}>
            <StringDisplay title="Beneficial owner type" value={capitalizeForEnumValue(beneficialOwnerType, true)} />
          </PaddedGridUnit>
          <PaddedGridUnit size={1 / 2}>
            <StringDisplay title="Beneficial owner role" value={capitalizeForEnumValue(beneficialOwnerRole)} />
          </PaddedGridUnit>
        </>
      )}
    </Grid>
  );
};

export const TrustApplicantSummaryLayout = (props: TrustApplicantSummaryLayoutProps) => {
  const { tracking } = useAnalytics();
  const { applicantIndex, goToSection, formValues } = props;
  const [opened, setOpened] = React.useState<boolean[]>([true, true]);

  const applicants = get(formValues, FIELD_PATHS.APPLICANTS(), []);
  const currentApplicantClientId = get(formValues, FIELD_PATHS.APPLICANT_CLIENT_ID(applicantIndex));
  const applicantType = get(formValues, FIELD_PATHS.APPLICANT_TYPE(applicantIndex));

  const trustName = get(formValues, FIELD_PATHS.APPLICANT_TRUST_NAME(applicantIndex));
  const trustType = capitalizeForEnumValue(get(formValues, FIELD_PATHS.APPLICANT_TRUST_TYPE(applicantIndex)));
  const registeredAddress = get(formValues, FIELD_PATHS.APPLICANT_TRUST_REGISTERED_ADDRESS_FORMATTED(applicantIndex));
  const mailingAddress = get(formValues, FIELD_PATHS.APPLICANT_TRUST_MAILING_ADDRESS_FORMATTED(applicantIndex));
  const registeredDateString = getDisplayDateString(
    get(formValues, FIELD_PATHS.APPLICANT_TRUST_REGISTERED_DATE(applicantIndex))
  );
  const registeredCountry = get(formValues, FIELD_PATHS.APPLICANT_TRUST_REGISTERED_COUNTRY(applicantIndex));
  const industryCode = get(formValues, FIELD_PATHS.APPLICANT_TRUST_INDUSTRY_CODE(applicantIndex));
  const relatedParties: RelatedParty[] = get(formValues, FIELD_PATHS.RELATED_PARTIES(), []);
  const trustees = relatedParties.filter(
    (relatedParty) =>
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
        TrustApplicantRelatedPartyType.Trustee &&
      get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TRUST_APPLICANT_ID(0)) ===
        currentApplicantClientId
  );

  const isTrustInformationCompleted = useMemo(
    () => !!applicantType && !!registeredDateString && !!registeredCountry && !!industryCode,
    [applicantType, registeredDateString, registeredCountry, industryCode]
  );

  const isTrusteeDetailsCompleted = useMemo(() => {
    let hasOneControllerType = false;
    for (const trustee of trustees) {
      const trusteeApplicantId = get({ relatedParties: [trustee] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0));
      const trustBeneficialOwner = relatedParties.find(
        (relatedParty) =>
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_APPLICANT_ID(0)) === trusteeApplicantId &&
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TRUST_APPLICANT_ID(0)) ===
            currentApplicantClientId &&
          get({ relatedParties: [relatedParty] }, FIELD_PATHS.RELATED_PARTY_TYPE(0)) ===
            TrustApplicantRelatedPartyType.TrustBeneficialOwner
      );

      const beneficialOwnerRole = get(
        { relatedParties: [trustBeneficialOwner] },
        FIELD_PATHS.RELATED_PARTY_OWNER_ROLE(0)
      );
      const beneficialOwnerType = get(
        { relatedParties: [trustBeneficialOwner] },
        FIELD_PATHS.RELATED_PARTY_OWNER_TYPE(0)
      );

      // if BeneficialOwner is checked need to finish beneficialOwnerRole and beneficialOwnerType
      if (!!trustBeneficialOwner && (!beneficialOwnerRole || !beneficialOwnerType)) {
        return false;
      }

      if (beneficialOwnerType === BeneficialOwnerType.Controller) {
        hasOneControllerType = true;
      }
    }

    // for the trust to be complete, it must have at least 1 controller trustee on the application
    return hasOneControllerType && trustees.length > 0;
  }, [trustees, relatedParties, currentApplicantClientId]);

  function handleAccordionClick(index: number, title: string) {
    const arr = [...opened];
    tracking?.accordion[arr[index] ? 'collapsed' : 'expanded']({ name: title, position: title });
    arr[index] = !arr[index]; // toggle
    setOpened(arr);
  }

  return (
    <Grid gutter="nil">
      <PaddedGridUnit size={1}>
        <GreyedPanelText>Trust details</GreyedPanelText>
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <StringDisplay title="Trust name" value={trustName} className={PII_FIELD_MASKING_CLASS} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <StringDisplay title="Trust type" value={trustType} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <StringDisplay title="Trust registered address" value={registeredAddress} className={PII_FIELD_MASKING_CLASS} />
      </PaddedGridUnit>
      <PaddedGridUnit size={1 / 2}>
        <StringDisplay title="Trust mailing address" value={mailingAddress} className={PII_FIELD_MASKING_CLASS} />
      </PaddedGridUnit>
      <AccordionWrapper>
        <AccordionGroup>
          {/* Trust information */}
          <Accordion isOpen={opened[0]}>
            <Accordion.Header
              after={<AccordionHeaderCircleIcon width="12px" height="12px" isComplete={isTrustInformationCompleted} />}
              onClick={() => handleAccordionClick(0, 'Trust information')}
              data-component="accordion-header"
            >
              <Heading size="xxs">Trust information</Heading>
            </Accordion.Header>
            <Accordion.Content data-component="accordion-content">
              <AccordionContent>
                <Grid gutter="nil">
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Trust registered date" value={registeredDateString} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Trust registered country" value={registeredCountry} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1 / 2}>
                    <StringDisplay title="Industry code" value={industryCode} />
                  </PaddedGridUnit>
                  <PaddedGridUnit size={1}>
                    <LinkContainer>
                      <Link onClick={() => goToSection(currentApplicantClientId, 'edit')}>View all or edit</Link>
                    </LinkContainer>
                  </PaddedGridUnit>
                </Grid>
              </AccordionContent>
            </Accordion.Content>
          </Accordion>

          {/* Trustee details */}
          <Accordion isOpen={opened[1]}>
            <Accordion.Header
              after={<AccordionHeaderCircleIcon width="12px" height="12px" isComplete={isTrusteeDetailsCompleted} />}
              onClick={() => handleAccordionClick(1, 'Trustee details')}
              data-component="accordion-header"
            >
              <Heading size="xxs">Trustee details</Heading>
            </Accordion.Header>
            <Accordion.Content data-component="accordion-content">
              <AccordionContent>
                {trustees.map((trustee: RelatedParty, index) => (
                  <TrustTrustees
                    key={`trustee-${trustee.clientId}`}
                    trustee={trustee}
                    relatedParties={relatedParties}
                    currentApplicantClientId={currentApplicantClientId}
                    applicants={applicants}
                    index={index}
                  />
                ))}
                <LinkContainer>
                  <Link onClick={() => goToSection(currentApplicantClientId, 'edit')}>View all or edit</Link>
                </LinkContainer>
              </AccordionContent>
            </Accordion.Content>
          </Accordion>
        </AccordionGroup>
      </AccordionWrapper>
    </Grid>
  );
};
